/* ═══════════════════════════════════════════
   Axios instance with CSRF + session support
   + response envelope normalization (Wave 4)
   ═══════════════════════════════════════════ */

import axios, { AxiosError } from 'axios'
import { useAuthStore } from '@/stores/auth.store'
import { isSaasApp } from '@/app/flavor'

const API_BASE = import.meta.env.VITE_API_URL || ''

function isLocalDevHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1'
}

/** Backend origin for API calls (SaaS dev → :8081, single-tenant dev → :8080). */
export function resolveApiBaseURL(): string {
  if (API_BASE) return API_BASE
  if (typeof window !== 'undefined') {
    const { hostname, protocol, port } = window.location
    if (port === '5173' && isLocalDevHost(hostname)) {
      const saasPort = isSaasApp() ? '8081' : '8080'
      return `${protocol}//${hostname}:${saasPort}`
    }
    return window.location.origin
  }
  return ''
}

export const apiClient = axios.create({
  baseURL: resolveApiBaseURL(),
  withCredentials: true,
  // Don't set default Content-Type — axios auto-detects it
  // (application/json for objects, multipart/form-data for FormData)
})

export function shouldResetAuthForResponse(status: number | undefined, url: string): boolean {
  const isAuthEndpoint = url.includes('/login') || url.includes('/api/session')
  return status === 401 && !isAuthEndpoint
}

function resolveApiOrigin(): URL {
  const base = resolveApiBaseURL()
  if (base) {
    return new URL(base)
  }
  return new URL(window.location.origin)
}

export function buildWebSocketUrl(path: string): string {
  const base = resolveApiOrigin()
  const wsProto = base.protocol === 'https:' ? 'wss:' : 'ws:'
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${wsProto}//${base.host}${normalized}`
}

export function localAgentBaseUrl(): string {
  const explicit = import.meta.env.VITE_PRINT_AGENT_URL?.trim()
  if (explicit) return explicit.replace(/\/$/, '')
  if (window.location.port === '5173') return '/print-agent'
  return 'http://127.0.0.1:8888'
}

// ── CSRF token interceptor ───────────────────────
apiClient.interceptors.request.use((config) => {
  const csrfToken = getCookie('csrf_token')
  if (csrfToken && config.method && config.method !== 'get') {
    config.headers['X-CSRF-Token'] = csrfToken
  }
  return config
})

// ── Response envelope normalization ──────────────
// Backend uses two success patterns:
//   1. api_ok(dict) → {success:true, message, ...fields}  (flat merge)
//   2. api_ok(list) → {success:true, message, data:[...]}  (nested)
// Some endpoints also do: jsonify({success:true, data:{...}}) — same shape as #2
// And one error pattern:
//   api_error(msg)  → {success:false, error:msg}
//
// This interceptor:
//   - Rejects {success:false} as AxiosError (unified error handling)
//   - Unwraps {success:true, data:T} → response.data = T
//     ONLY when 'data' is the sole payload key (besides success/message).
//     If the response has extra keys (total, page, etc.) alongside 'data',
//     it's a flat-merge dict and is kept as-is.
//   - Keeps flat success responses as-is (result.success still works)
//   - Passes bare data through unchanged
apiClient.interceptors.response.use(
  (response) => {
    const raw = response.data
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'success' in raw) {
      if (raw.success === false) {
        const msg = raw.error || raw.message || 'API Error'
        const err = new AxiosError(msg, 'API_ERROR', response.config, response.request, response)
        // Forward structured error info (code/details) to callers.
        if (raw.code !== undefined) (err as unknown as { apiCode?: string }).apiCode = String(raw.code)
        if (raw.details !== undefined) (err as unknown as { apiDetails?: unknown }).apiDetails = raw.details
        return Promise.reject(err)
      }
      if (raw.success === true && 'data' in raw && raw.data !== undefined) {
        // Only unwrap if 'data' is the sole payload key.
        // api_ok(list) → {success, message, data} — unwrap.
        // api_ok({data: x, total: y}) → {success, message, data, total} — keep as-is.
        const payloadKeys = Object.keys(raw).filter(
          (k) => k !== 'success' && k !== 'message',
        )
        if (payloadKeys.length === 1 && payloadKeys[0] === 'data') {
          response.data = raw.data
        }
      }
    }
    return response
  },
  (error) => {
    const status = error?.response?.status
    const url = error?.config?.url || ''
    if (shouldResetAuthForResponse(status, url)) {
      const { isAuthenticated, clearAuth } = useAuthStore.getState()
      if (isAuthenticated) {
        clearAuth()
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  },
)

/** Extract a human-readable message from an API error (Axios or generic). */
export function extractApiError(err: unknown): string {
  if (err instanceof AxiosError) {
    const rd = err.response?.data
    if (typeof rd === 'string' && rd.trim()) return rd.trim()
    if (rd && typeof rd === 'object' && 'error' in rd) return String(rd.error)
    if (rd && typeof rd === 'object' && 'message' in rd) return String(rd.message)
    if (err.response?.status === 404) {
      return 'Эндпоинт не найден — перезапустите сервер с актуальной сборкой'
    }
    return err.message
  }
  if (err instanceof Error) return err.message
  return String(err)
}

/**
 * Extract structured API error info (message + optional code/details).
 * Code/details are attached by the response interceptor on `success:false` envelopes.
 */
export function extractApiErrorInfo(err: unknown): {
  message: string
  code?: string
  details?: Record<string, unknown>
} {
  const message = extractApiError(err)
  if (err instanceof AxiosError) {
    const code = (err as unknown as { apiCode?: string }).apiCode
    const details = (err as unknown as { apiDetails?: Record<string, unknown> }).apiDetails
    return { message, code, details }
  }
  return { message }
}

/** Flatten validation_error details into a readable message. */
export function formatValidationError(err: unknown): string {
  const { message, code, details } = extractApiErrorInfo(err)
  if (code === 'validation_error' && details && typeof details === 'object') {
    const parts = Object.values(details)
      .map((v) => (typeof v === 'string' ? v : String(v)))
      .filter(Boolean)
    if (parts.length > 0) {
      return parts.join('. ')
    }
  }
  return message || 'Ошибка валидации'
}

/** Print/API errors including multiple_gtins details.gtins list. */
export function formatPrintError(err: unknown): string {
  const { message, code, details } = extractApiErrorInfo(err)
  if (code === 'multiple_gtins' && details && typeof details === 'object') {
    const raw = details.gtins
    const gtins = Array.isArray(raw)
      ? raw.map((v) => String(v)).filter(Boolean)
      : []
    if (gtins.length > 0) {
      return `${message}: ${gtins.join(', ')}`
    }
  }
  return message
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}
